import type { Rule, RuleSet, UmaPreset } from './types.js';

/**
 * 4人打ちのウマのプリセット（§4.3）。
 * 名前は「2位/1位の増減」を表す。one_two なら 2位 +10 / 1位 +20。
 */
export const UMA_PRESETS_4P: Readonly<Record<Exclude<UmaPreset, 'custom'>, readonly number[]>> =
  Object.freeze({
    gotto: Object.freeze([10, 5, -5, -10]),
    one_two: Object.freeze([20, 10, -10, -20]),
    one_three: Object.freeze([30, 10, -10, -30]),
    two_three: Object.freeze([30, 20, -20, -30]),
  });

/** 4人打ちの既定ルール（§4.3 の既定値(4人)列） */
export const DEFAULT_RULE_4P: Readonly<Omit<RuleSet, 'ruleId'>> = Object.freeze({
  name: '標準ルール',
  playerCount: 4,
  startPoints: 25_000,
  returnPoints: 30_000,
  uma: [20, 10, -10, -20],
  umaPreset: 'one_two',
  rounding: 'gosha_rokunyu',
  zeroSumAdjust: 'top',
  bankruptcy: Object.freeze({
    enabled: true,
    point: 10,
    threshold: 'below_zero',
    payTo: 'buster',
    fallbackPayTo: 'top',
  }),
  endOnBankruptcy: true,
});

/**
 * 3人打ちの既定ルール（§4.3 の既定値(3人)列）。
 * 3人打ちのウマに名前付きプリセットはないため umaPreset は custom。
 */
export const DEFAULT_RULE_3P: Readonly<Omit<RuleSet, 'ruleId'>> = Object.freeze({
  name: '標準三麻',
  playerCount: 3,
  startPoints: 35_000,
  returnPoints: 40_000,
  uma: [20, 0, -20],
  umaPreset: 'custom',
  rounding: 'gosha_rokunyu',
  zeroSumAdjust: 'top',
  bankruptcy: Object.freeze({
    enabled: true,
    point: 10,
    threshold: 'below_zero',
    payTo: 'buster',
    fallbackPayTo: 'top',
  }),
  endOnBankruptcy: true,
});

export function defaultRule(playerCount: 3 | 4): Omit<RuleSet, 'ruleId'> {
  const base = playerCount === 3 ? DEFAULT_RULE_3P : DEFAULT_RULE_4P;
  // 定数は Object.freeze されているため、呼び出し側が編集できるコピーを返す。
  // structuredClone は使わない: このパッケージはブラウザと Lambda の両方から
  // 読み込まれるので、環境依存のグローバル API に型ごと依存させたくない。
  // ネストは bankruptcy と uma の1階層だけなので手で写して十分。
  return {
    ...base,
    uma: [...base.uma],
    bankruptcy: { ...base.bankruptcy },
  };
}

/**
 * ウマの妥当性（§4.3「順位順の配列。合計は0であること」）。
 * ゼロサムが崩れると全員の合計が 0 にならなくなるため、ルール保存前に必ず確認する。
 */
export function isValidUma(uma: readonly number[], playerCount: 3 | 4): boolean {
  if (uma.length !== playerCount) return false;
  if (!uma.every((n) => Number.isFinite(n))) return false;
  return Math.abs(uma.reduce((a, b) => a + b, 0)) < 1e-9;
}

/** ルールセットから計算に必要な部分だけを取り出す（対局へのスナップショット用） */
export function toRuleSnapshot(rule: RuleSet): Rule {
  return {
    playerCount: rule.playerCount,
    startPoints: rule.startPoints,
    returnPoints: rule.returnPoints,
    uma: [...rule.uma],
    rounding: rule.rounding,
    zeroSumAdjust: rule.zeroSumAdjust,
    bankruptcy: { ...rule.bankruptcy },
  };
}
